<template>
    <div>
        <cc-modal :modal="modal" @close="close">
            <div slot="body" class="permission-modal">
                <div class="permission-modal__header">
                    <div class="permission-modal__header-info">
                        <cc-input v-model="loadedProfile.name" label="Nome do Perfil" :disabled="loading" placeholder="Ex: Financeiro, Vendas..." />
                    </div>
                    <button
                        class="profiles-page__ghost-button save-btn"
                        :disabled="loading || !loadedProfile.name"
                        @click="saveProfileName"
                    >
                        <i class="fas fa-save"></i>
                        Salvar Nome
                    </button>
                </div>

                <div class="permission-modal__grid" v-if="loadedProfile.id">
                    <section class="permission-modal__panel">
                        <label class="label-control">Módulos</label>
                        <div class="sellers-list list-group">
                            <cc-loader v-show="loading"></cc-loader>
                            <table v-show="!loading" class="table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th class="text-left px-3">Módulo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="module in current_modules"
                                        :key="'perm-res-' + module.resource"
                                        :class="{
                                            selected: isSelectedModule(module),
                                            'permission-modal__row--loading': isPermissionLoading(module),
                                        }"
                                    >
                                        <td class="permission-modal__checkbox-cell" @click.stop>
                                            <span v-if="isPermissionLoading(module)" class="permission-modal__spinner" aria-hidden="true" />
                                            <div v-else class="checkbox">
                                                <cc-checkbox :value="has_permission(module)" @input="togglePermission(module, $event)" />
                                            </div>
                                        </td>
                                        <td class="text-left px-3" @click="select_module(module)">
                                            <div class="permission-modal__module-name">{{ module.name }}</div>
                                            <small class="permission-modal__module-meta">
                                                {{ translateAction(module.action) }}
                                            </small>
                                        </td>
                                    </tr>
                                    <tr v-if="!loading && current_modules.length === 0">
                                        <td colspan="2" class="permission-modal__empty-state">
                                            Nenhuma permissão disponível.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section class="permission-modal__panel">
                        <label class="label-control">{{ current_module.name || "Permissões" }}</label>
                        <div class="sellers-list list-group">
                            <cc-loader v-show="loading"></cc-loader>
                            <table v-show="!loading" class="table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th class="text-left px-3">Permissão</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="permission in current_module.permissions"
                                        :key="'perm-' + permissionKey(permission)"
                                        :class="{
                                            selected: has_permission(permission),
                                            'permission-modal__row--loading': isPermissionLoading(permission),
                                        }"
                                        @click="togglePermission(permission)"
                                    >
                                        <td class="permission-modal__status-cell">
                                            <span
                                                v-if="isPermissionLoading(permission)"
                                                class="permission-modal__spinner"
                                                aria-hidden="true"
                                            />
                                            <i v-else class="fa fa-check success-update" :class="{ visible: has_permission(permission) }" />
                                        </td>
                                        <td class="text-left px-3">
                                            <div class="change-status">
                                                <div class="permission-modal__permission-header">
                                                    <p class="perm-name">
                                                        {{ permission.name || translateAction(permission.action) }}
                                                    </p>
                                                    <span class="permission-modal__permission-action">
                                                        {{ translateAction(permission.action) }}
                                                    </span>
                                                </div>
                                                <small>{{ permission.description || translateAction(permission.action) }}</small>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="!loading && current_module.permissions.length === 0">
                                        <td colspan="2" class="permission-modal__empty-state">
                                            Nenhuma permissão disponível para este módulo.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </div>
        </cc-modal>
    </div>
</template>

<script>
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ProfileManagementService from "@/services/v3/management/profile.service";
import PermissionManagementService from "@/services/v3/management/permission.service";
import {
    buildPermissionLookup,
    buildPermissionModules,
    extractCollectionFromPayload,
    extractItemFromPayload,
    filterPermissionsByVisibility,
    getPermissionKey,
    normalizePermissionRecord,
    translatePermissionAction,
} from "@/helpers/permissions";

const EMPTY_MODULE = {
    name: "",
    resource: "",
    action: "",
    permissions: [],
};

export default {
    mixins: [loaderMixin],
    props: {
        profile: { type: Object, required: true },
        closeDialog: { type: Function },
    },
    data() {
        return {
            loadedProfile: { ...this.profile, permissions: [] },
            availablePermissions: [],
            current_module_resource: null,
            loading: false,
            permissionLoadingMap: {},
            profileManagementSvc: new ProfileManagementService(),
            permissionManagementSvc: new PermissionManagementService(),
            modal: {
                title: "Editar Perfil",
                subtitle: this.profile.name,
                icon_title: "fas fa-user-edit",
                cancel_text: "Voltar",
                style: {
                    width: "96vw",
                    "min-height": "110vh",
                },
            },
        };
    },
    computed: {
        permissionVisibility() {
            return this.loadedProfile.visibility === "all" ? null : this.loadedProfile.visibility;
        },
        current_modules() {
            return buildPermissionModules(filterPermissionsByVisibility(this.availablePermissions, this.permissionVisibility));
        },
        current_module() {
            if (!this.current_modules.length) {
                return EMPTY_MODULE;
            }
            return this.current_modules.find((module) => module.resource === this.current_module_resource) || this.current_modules[0];
        },
        selectedPermissionKeys() {
            return buildPermissionLookup(this.loadedProfile.permissions || []);
        },
    },
    methods: {
        translateAction(action) {
            return translatePermissionAction(action);
        },
        permissionKey(permission) {
            return getPermissionKey(permission);
        },
        permissionLoadingKey(permission) {
            return `${this.loadedProfile.id}::${this.permissionKey(permission)}`;
        },
        setPermissionLoading(permission, isLoading) {
            const key = this.permissionLoadingKey(permission);
            if (isLoading) {
                this.$set(this.permissionLoadingMap, key, true);
                return;
            }
            this.$delete(this.permissionLoadingMap, key);
        },
        ensureCurrentModuleSelection() {
            if (!this.current_modules.length) {
                this.current_module_resource = null;
                return;
            }
            const selectedModuleExists = this.current_modules.some((module) => module.resource === this.current_module_resource);
            if (!selectedModuleExists) {
                this.current_module_resource = this.current_modules[0].resource;
            }
        },
        isSelectedModule(module) {
            return module.resource === this.current_module.resource;
        },
        isPermissionLoading(permission) {
            return !!this.permissionLoadingMap[this.permissionLoadingKey(permission)];
        },
        has_permission(permission) {
            return this.selectedPermissionKeys.has(this.permissionKey(permission));
        },
        select_module(module) {
            this.current_module_resource = module.resource;
        },
        async loadPermissionCatalog() {
            const response = await this.permissionManagementSvc.list();
            this.availablePermissions = extractCollectionFromPayload(response.data).map((permission) =>
                normalizePermissionRecord(permission)
            );
            this.ensureCurrentModuleSelection();
        },
        async loadProfileInfo() {
            const response = await this.profileManagementSvc.list({ id: this.profile.id, with_permissions: true });
            const profiles = extractCollectionFromPayload(response.data);
            if (profiles.length > 0) {
                this.loadedProfile = { ...this.loadedProfile, ...profiles[0] };
            }
            this.ensureCurrentModuleSelection();
        },
        async load() {
            this.loading = true;
            try {
                await Promise.all([this.loadPermissionCatalog(), this.loadProfileInfo()]);
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loading = false;
            }
        },
        async saveProfileName() {
            this.loading = true;
            try {
                await this.profileManagementSvc.update(this.loadedProfile.id, { name: this.loadedProfile.name });
                this.modal.subtitle = this.loadedProfile.name;
                this.$store.dispatch("notification/add", {
                    type: "success",
                    message: "Nome atualizado com sucesso!",
                });
                this.$emit("profile-updated");
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.loading = false;
            }
        },
        async updateProfilePermission(permission, enabled) {
            if (!this.loadedProfile.id || !permission.id || this.isPermissionLoading(permission)) {
                return;
            }

            this.setPermissionLoading(permission, true);

            try {
                const response = await this.profileManagementSvc.togglePermission(this.loadedProfile.id, permission.id, enabled);
                const updatedProfile = extractItemFromPayload(response.data);
                if (updatedProfile && updatedProfile.permissions) {
                    this.loadedProfile.permissions = updatedProfile.permissions;
                }
                this.$store.dispatch("notification/add", {
                    type: "success",
                    message: enabled ? "Permissão adicionada com sucesso!" : "Permissão removida com sucesso!",
                });
            } catch (error) {
                ErrorHandlerService.handle(error, this.$store);
            } finally {
                this.setPermissionLoading(permission, false);
            }
        },
        togglePermission(permission, enabled) {
            if (this.isPermissionLoading(permission)) {
                return;
            }
            const targetState = typeof enabled === "boolean" ? enabled : !this.has_permission(permission);
            if (targetState === this.has_permission(permission)) {
                return;
            }
            return this.updateProfilePermission(permission, targetState);
        },
        close() {
            this.closeDialog();
        },
    },
    mounted() {
        this.load();
    },
};
</script>

<style lang="scss" scoped>
@import "./index.modal";

.permission-modal {
    min-height: min(70vh, 760px);
}

.permission-modal__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.5rem;
    margin-bottom: 2rem;
    padding: 1.25rem;
    background: #f8faff;
    border-radius: 12px;
    border: 1px solid #e1e8f5;

    .permission-modal__header-info {
        flex: 1;
        min-width: 0;
    }

    .save-btn {
        margin-bottom: 4px;
        padding: 0.75rem 1.5rem;
        background: #355ca8;
        color: #fff;
        border: none;
        font-weight: 700;
        border-radius: 10px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        transition: all 0.2s ease;

        &:hover:not(:disabled) {
            background: #2a4a86;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(53, 92, 168, 0.2);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
}

.permission-modal__grid {
    display: grid;
    grid-template-columns: minmax(250px, 1fr) minmax(340px, 1.2fr);
    gap: 1.25rem;
    align-items: start;
}

.permission-modal__panel {
    min-width: 0;
}

.permission-modal__checkbox-cell,
.permission-modal__status-cell {
    width: 3rem;
}

.permission-modal__row--loading {
    opacity: 0.82;
    background: linear-gradient(90deg, rgba(53, 92, 168, 0.04), rgba(53, 92, 168, 0.1), rgba(53, 92, 168, 0.04));
    background-size: 200% 100%;
    animation: permission-modal-row-loading 1.1s ease-in-out infinite;
}

.permission-modal__spinner {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    border: 2px solid #dbe4f5;
    border-top-color: #355ca8;
    border-radius: 50%;
    animation: permission-modal-spin 0.7s linear infinite;
}

.permission-modal__module-name,
.permission-modal__permission-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.permission-modal__module-meta,
.permission-modal__permission-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    background: #eef3ff;
    color: #355ca8;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
}

.permission-modal__empty-state {
    color: #6e6e6e;
    text-align: center;
}

.change-status {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
}

.perm-name {
    margin: 0;
    font-weight: 600;
}

::v-deep .modal-body {
    max-height: none !important;
}

::v-deep .modal-container {
    padding: 20px 32px;
}

::v-deep .sellers-list {
    height: clamp(24rem, 58vh, 36rem);
    border: 1px solid #ececec;
    border-radius: 12px;
    overflow: auto;
}

::v-deep .sellers-list .table {
    margin-bottom: 0;
}

::v-deep .sellers-list .table th {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
}

::v-deep .sellers-list .selected {
    background: #f5f8ff;
}

@keyframes permission-modal-spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes permission-modal-row-loading {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

@media only screen and (max-width: 768px) {
    .permission-modal__grid {
        grid-template-columns: 1fr;
    }

    .permission-modal {
        min-height: auto;
    }

    ::v-deep .modal-container {
        width: 96vw !important;
        padding: 20px;
    }
}
</style>
